import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../../firebase";
import { collection, getDocs } from "firebase/firestore";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { exportToExcel } from "../../utils/exportToExcel";

const stripTags = (value = "") =>
  value
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .trim();

const extractValue = (html, label) => {
  if (!html) return "";
  const patterns = [
    new RegExp(
      `<td[^>]*?>\\s*${label}:\\s*</td>\\s*<td[^>]*?>([\\s\\S]*?)</td>`,
      "i"
    ),
    new RegExp(`<strong>\\s*${label}:?\\s*</strong>:?\\s*([\\s\\S]*?)</p>`, "i"),
  ];
  for (const regex of patterns) {
    const match = html.match(regex);
    if (match && stripTags(match[1])) return stripTags(match[1]);
  }
  return "";
};

const toEnquiry = (docSnap) => {
  const raw = docSnap.data();
  const message = raw.message || {};
  const delivery = raw.delivery || {};
  const stored = raw.enquiry || {};
  const html = message.html || "";
  const subject = message.subject || "";

  const idTime = Number(docSnap.id);
  const createdAt =
    stored.createdAt ||
    (Number.isFinite(idTime) ? idTime : null) ||
    delivery.startTime?.toMillis?.() ||
    null;

  const nameFromGreeting = (html.match(/Dear\s*<strong>(.*?)<\/strong>/i) ||
    [])[1];
  const nameFromSubject = subject.includes(",")
    ? subject.split(",").pop().trim()
    : "";

  return {
    id: docSnap.id,
    createdAt,
    type: stored.type || (/portfolio/i.test(subject) ? "Portfolio" : "Query"),
    name:
      stored.name ||
      extractValue(html, "Name") ||
      stripTags(nameFromGreeting || "") ||
      nameFromSubject,
    email: stored.email || extractValue(html, "Email") || raw.to || "",
    mobile: stored.mobile || extractValue(html, "Mobile"),
    country: stored.country || extractValue(html, "Country"),
    service: stored.service || extractValue(html, "Service"),
    query:
      stored.query ||
      extractValue(html, "Query") ||
      extractValue(html, "Message"),
    subject,
    status: delivery.state || "UNKNOWN",
    error: delivery.error || "",
  };
};

const formatDate = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/customers/login");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const confirmLogout = () => setShowConfirm(true);
  const cancelLogout = () => setShowConfirm(false);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/customers/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const querySnapshot = await getDocs(collection(db, "mail"));
      const data = querySnapshot.docs
        .map(toEnquiry)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setCustomers(data);
    } catch (error) {
      console.error("Failed to load enquiries:", error);
      setLoadError(
        error.code === "permission-denied"
          ? "Permission denied: Firestore rules don't allow this account to read the 'mail' collection."
          : error.message || "Failed to load enquiries."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredCustomers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return customers.filter((c) => {
      if (typeFilter !== "All" && c.type !== typeFilter) return false;
      if (!term) return true;
      return [c.name, c.email, c.mobile, c.country, c.service, c.query]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [customers, search, typeFilter]);

  const prepareExport = () => {
    const exportData = filteredCustomers.map((c) => ({
      Date: formatDate(c.createdAt),
      Type: c.type,
      Name: c.name,
      Mobile: c.mobile,
      Email: c.email,
      Country: c.country,
      Service: c.service,
      Query: c.query,
      Subject: c.subject,
      DeliveryStatus: c.status,
    }));

    exportToExcel(exportData, "CustomerSubmissions");
  };

  // ----------- MODAL STYLES -----------
  const modalStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1000,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const modalBoxStyle = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "10px",
    textAlign: "center",
    color: "#000",
    width: "90%",
    maxWidth: "400px",
  };

  const modalButton = {
    margin: "10px",
    padding: "8px 16px",
    border: "none",
    borderRadius: "5px",
    fontWeight: "bold",
    cursor: "pointer",
  };

  const containerStyle = {
    padding: "20px",
    backgroundColor: "#0e1a2b",
    color: "#ffffff",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', sans-serif",
  };

  const headerStyle = {
    marginBottom: "20px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "10px",
  };

  const toolbarStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    marginBottom: "15px",
    alignItems: "center",
  };

  const inputStyle = {
    padding: "8px 12px",
    borderRadius: "5px",
    border: "1px solid #2c3e50",
    backgroundColor: "#1c2a3a",
    color: "#ffffff",
    fontSize: "14px",
  };

  const buttonStyle = {
    padding: "8px 12px",
    backgroundColor: "#ff9d00",
    color: "#000",
    border: "none",
    borderRadius: "5px",
    fontWeight: "bold",
    cursor: "pointer",
    width: "fit-content",
  };

  const linkStyle = { color: "#ff9d00" };

  const tableWrapperStyle = {
    overflowX: "auto",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: "#1c2a3a",
    fontSize: "14px",
    minWidth: "900px",
  };

  const thStyle = {
    padding: "12px",
    textAlign: "left",
    border: "1px solid #2c3e50",
    backgroundColor: "#243447",
    fontWeight: "bold",
    color: "#ffffff",
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "12px",
    textAlign: "left",
    border: "1px solid #2c3e50",
    color: "#e0e0e0",
    maxWidth: "250px",
    overflowWrap: "anywhere",
    verticalAlign: "top",
  };

  const statusStyle = (status) => ({
    color: status === "SUCCESS" ? "#2ecc71" : "#e74c3c",
    fontWeight: "bold",
  });

  return (
    <div style={containerStyle}>
      {showConfirm && (
        <div style={modalStyle}>
          <div style={modalBoxStyle}>
            <p>Are you sure you want to logout?</p>
            <div style={{ marginTop: "15px" }}>
              <button
                style={{
                  ...modalButton,
                  backgroundColor: "#e74c3c",
                  color: "#fff",
                }}
                onClick={handleLogout}
              >
                Yes, Logout
              </button>
              <button
                style={{ ...modalButton, backgroundColor: "#ccc" }}
                onClick={cancelLogout}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={headerStyle}>
        <h2 style={{ margin: 0 }}>
          Customer Enquiries{" "}
          <span style={{ color: "#ff9d00", fontSize: "16px" }}>
            ({filteredCustomers.length}
            {filteredCustomers.length !== customers.length &&
              ` of ${customers.length}`}
            )
          </span>
        </h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          <button style={buttonStyle} onClick={fetchData} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button
            style={buttonStyle}
            onClick={prepareExport}
            disabled={!filteredCustomers.length}
          >
            Download Excel
          </button>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: "#e74c3c",
              color: "#fff",
            }}
            onClick={confirmLogout}
          >
            Logout
          </button>
        </div>
      </div>

      <div style={toolbarStyle}>
        <input
          type="text"
          placeholder="Search name, email, mobile, query..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: "1 1 260px", maxWidth: "400px" }}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={inputStyle}
        >
          <option value="All">All types</option>
          <option value="Query">Query</option>
          <option value="Portfolio">Portfolio</option>
        </select>
      </div>

      {loadError && (
        <p style={{ color: "#e74c3c", fontWeight: "bold" }}>{loadError}</p>
      )}

      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>#</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Mobile</th>
              <th style={thStyle}>Country</th>
              <th style={thStyle}>Service / Query</th>
              <th style={thStyle}>Mail Status</th>
            </tr>
          </thead>
          <tbody>
            {!loading && !loadError && filteredCustomers.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center" }} colSpan={9}>
                  No enquiries found.
                </td>
              </tr>
            )}
            {filteredCustomers.map((c, i) => (
              <tr key={c.id}>
                <td style={tdStyle}>{i + 1}</td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  {formatDate(c.createdAt)}
                </td>
                <td style={tdStyle}>{c.type}</td>
                <td style={tdStyle}>{c.name || "-"}</td>
                <td style={tdStyle}>
                  {c.email ? (
                    <a href={`mailto:${c.email}`} style={linkStyle}>
                      {c.email}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                  {c.mobile ? (
                    <a href={`tel:${c.mobile}`} style={linkStyle}>
                      {c.mobile}
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td style={tdStyle}>{c.country || "-"}</td>
                <td style={tdStyle}>
                  {c.service && <strong>{c.service}</strong>}
                  {c.service && c.query && <br />}
                  {c.query || (!c.service && "-")}
                </td>
                <td
                  style={{ ...tdStyle, ...statusStyle(c.status) }}
                  title={c.error}
                >
                  {c.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

